import styled, { css } from "styled-components";
import { firestore } from "@musicaudienceexchange/ap-interface";
import { formatPhoneNumber } from "Utils";
import Skeleton from "react-loading-skeleton";
import { useDesktopMediaQuery } from "hooks";
import { ReactComponent as PhoneIcon } from "assets/svg/phone.svg";
import { ReactComponent as EmailIcon } from "assets/svg/email.svg";
import { useState } from "react";
import { useUser } from "auth";
import { useArtistContext } from "Components/ArtistProvider";
import { toast } from "react-hot-toast";
import { getPiiData, PiiKey } from "Utils/secure";

const { Fan } = firestore;

export const FanRow = ({ fan, isSecure }) => {
  const fanModel = Fan.raw(fan, { silent: true });
  const { age, location, name, gender, createdAt } = fanModel;
  const [email, setEmail] = useState(isSecure ? "" : fanModel.email);
  const [phone, setPhone] = useState(isSecure ? "" : fanModel.phone);
  const [loadingPhone, setLoadingPhone] = useState(false);
  const [loadingEmail, setLoadingEmail] = useState(false);
  const isDesktop = useDesktopMediaQuery();
  const { claims } = useUser();
  const isAdmin = claims.admin;
  const { id: artistGroupId } = useArtistContext();

  const handlePiiDataClick = async (key: PiiKey) => {
    try {
      const value = await getPiiData(
        artistGroupId,
        key,
        fan.id.split(":")[1],
        key === "email" ? setLoadingEmail : setLoadingPhone,
      );
      (key === "phone" ? setPhone : setEmail)(value);
    } catch (error) {
      toast.error("There was an error fetching fan data");
      console.error(error);
    }
  };

  return (
    <Container>
      <Element data-sentry-mask>{name}</Element>
      <Element>{location}</Element>
      {isDesktop && (
        <>
          <Element data-sentry-mask>{age === 0 ? "" : age}</Element>
          <Element data-sentry-mask>{gender}</Element>
          <Element>{createdAt?.setZone("local")?.toFormat("DD")}</Element>
        </>
      )}
      <Element data-sentry-mask>
        <PiiData isAdmin={isAdmin}>
          {loadingPhone ? (
            <Skeleton />
          ) : isSecure && fanModel.phone && !phone ? (
            <PhoneIcon
              {...(isAdmin && { onClick: () => handlePiiDataClick("phone") })}
            />
          ) : (
            <span>{formatPhoneNumber(phone)}</span>
          )}
        </PiiData>
      </Element>
      <Element data-sentry-mask>
        <PiiData isAdmin={isAdmin}>
          {loadingEmail ? (
            <Skeleton />
          ) : isSecure && fanModel.email && !email ? (
            <EmailIcon
              {...(isAdmin && { onClick: () => handlePiiDataClick("email") })}
            />
          ) : (
            <span>{email}</span>
          )}
        </PiiData>
      </Element>
    </Container>
  );
};

export const FanRowLoader = () => (
  <Container>
    <Element>
      <Skeleton />
    </Element>
    <Element>
      <Skeleton />
    </Element>
    <Element>
      <Skeleton />
    </Element>
    <Element>
      <Skeleton />
    </Element>
    <Element>
      <Skeleton />
    </Element>
    <Element>
      <Skeleton />
    </Element>
    <Element>
      <Skeleton />
    </Element>
  </Container>
);

const Container = styled.div(({ theme }) => {
  const { border, hover } = theme.custom?.pages.contacts.table.row || {};
  return css`
    display: flex;
    flex-direction: column;
    padding: 12px 16px;
    box-shadow: inset 0 -1px 0 0 ${border?.color || "#e6e9eb"};

    &:hover {
      background-color: ${hover?.background || "#f2f5f7"};
    }

    ${theme.media.desktop} {
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
    }
  `;
});

const Element = styled.div`
  color: inherit;
  font-family: Poppins;
  font-size: 14px;
  letter-spacing: 0;
  line-height: 21px;
`;

interface PiiDataProps {
  isAdmin: boolean;
}

const PiiData = styled.div<PiiDataProps>`
  display: flex;
  width: 100%;

  ${({ theme }) => theme.media.desktop} {
    height: 24px;

    span {
      display: flex;
      align-items: center;
      width: 100%;
    }
  }

  svg {
    ${({ isAdmin }) =>
      isAdmin
        ? css`
            cursor: pointer;
          `
        : css`
            color: var(--disabled-color);
          `}

    ${({ theme }) => theme.media.mobile} {
      height: 23px;
      padding: 1px 0;
    }
  }
`;
